import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { useTranslation } from 'react-i18next';

// Redux 

import { setCurrentFolder, setFoldersList, selectFolder, selectFile, setDocumentsFolder, setDownloadsFolder, setPatientsFolder, setMode } from '../../../../../redux/features/doctor/Dashboard/documents/fileExplorer';
import { setIsOpened } from '../../../../../redux/features/doctor/Dashboard/documents/createFolder';

// Routes

import { getDoctorRootFolders } from '../../../../../routes/doctor/files';

// Utils 

import protectedPaths from './protectedPaths';

// Components

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import FolderRow from './FolderRow';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { resetSavedConsultation, setSection } from '../../../../../redux/features/doctor/Dashboard/consultation/savedConsultation';

const FoldersList: React.FC = () => {

    const dispatch = useAppDispatch(); 
    const { t } = useTranslation();

    const refreshFileExplorer = useAppSelector((state) => state.fileExplorer.refreshFileExplorer); 
    const mode = useAppSelector((state) => state.fileExplorer.mode);    
    const foldersList = useAppSelector((state) => state.fileExplorer.foldersList); 
    const currentFolder = useAppSelector((state) => state.fileExplorer.currentFolder); // Get current folder for highlighting

    // -- 

    const changeSection = (selectedFolder: any) => { 

        dispatch(setCurrentFolder(selectedFolder)); 

        if (!protectedPaths.includes(selectedFolder.path)) { 

            // Only deselect file/folder if not in a special mode
            if (!mode) {
                dispatch(selectFolder(selectedFolder)); 
                dispatch(selectFile(null)); 
            }

        }; 

        // Reset mode if changing folder while in these modes
        if ((mode === 'sharedFolder') || (mode === 'consultations') || (mode === 'trash')) { 
            dispatch(setMode('')); 
            dispatch(resetSavedConsultation()); 
        }; 


    }; 

    // --

    const getFoldersList = async () => { 

        const res = await getDoctorRootFolders() as any; 

        if (res?.data.status === '200') { 

            dispatch(setFoldersList((res.data.dataList))); 

            // For Redux purposes

            res.data.dataList?.forEach((folder: any) => { 

                if (folder?.path === 'Patients') { 

                    dispatch(setPatientsFolder(folder)); 

                } else if (folder?.path === 'Downloads') { 

                    dispatch(setDownloadsFolder(folder)); 

                } else if (folder?.path === 'Documents') { 

                    dispatch(setDocumentsFolder(folder)); 

                }; 

            }); 

        }; 

    }; 

    // -- 

    useEffect(() => { 

        // Fetch folders if list is empty or undefined
        if (!foldersList || foldersList.length === 0) { 
             getFoldersList(); 
        }; 

    },[foldersList]); 

    // -- 

    useEffect(() => { 

        if (refreshFileExplorer) { 

            getFoldersList(); 

        }; 

    },[refreshFileExplorer]); 

    // -- 

    return (

        <Box
            sx={{ 
                display: 'flex',
                flexDirection: 'column', 
                alignItems: 'center',
                width: '100%',
                // height: '90%', // Let flexbox handle height
                flexGrow: 1, // Allow box to grow and fill space
                marginTop: 1, // Reduce top margin
                overflow: 'hidden', // Parent handles scroll
            }}
        >

        { foldersList?.length >= 1 && 

            <Box 
                sx={{ 
                overflowY: 'auto', // Enable scrolling within this Box if needed
                overflowX: 'hidden',
                width: '100%',
                height: '100%', // Take full height of parent
                scrollbarWidth: 'thin', // Use thin scrollbar
                '&::-webkit-scrollbar': { // Webkit scrollbar styling
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,.2)',
                    borderRadius: '3px',
                  }
                }}
            >
        
                <List dense disablePadding>
        
                {foldersList?.map((item, index) => (
                    item && (
                        <ListItem 
                            onClick={() => changeSection(item)} 
                            button 
                            key={item._id || index} // Use _id as key if available
                            disablePadding
                            sx={{ 
                                // Highlight the currently selected folder
                                backgroundColor: currentFolder?._id === item._id ? 'action.hover' : 'transparent',
                                paddingY: 0.5, // Add some vertical padding to list items
                             }}
                        >
                            <FolderRow 
                                _id={item._id}
                                path={item.path}
                                title={item.title}
                                description={item.description}
                                status={item.status}
                                folder={item.folder} 
                                uploadedBy={item.uploadedBy}
                                relatedPatient={item.relatedPatient}
                                pinned={item.pinned}
                                createdAt={item.createdAt}
                                updatedAt={item.updatedAt}
                            />
                        </ListItem>
                    )
                ))}
        
                </List>
        
            </Box>}

          </Box>

    );

};

export default FoldersList;